import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, MapPin, Trash2, Plus, LogOut, User, Phone, Mail, Star, Clock, Repeat, Navigation, Radio, Truck, Gift, Copy, Smartphone, HelpCircle, XCircle, Pencil } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import EditRouteDialog from '@/components/EditRouteDialog';
import { API } from '@/lib/api';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import PushToggle from '@/components/PushToggle';
import SubscriptionCard from '@/components/SubscriptionCard';
import RatingTipDialog from '@/components/RatingTipDialog';
import LoyaltyCreditsCard from '@/components/LoyaltyCreditsCard';
import AvatarUploader from '@/components/AvatarUploader';

export default function Account() {
  const navigate = useNavigate();
  const { user, logout, loading: authLoading, patchUser } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newAddr, setNewAddr] = useState({ address: '', coords: null, city: '', state: '' });
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [rateTarget, setRateTarget] = useState(null);
  const [editRouteBooking, setEditRouteBooking] = useState(null);

  // Referral code (minted lazily on demand, idempotent)
  const [referral, setReferral] = useState(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const fetchOrMintReferral = async () => {
    setReferralLoading(true);
    try {
      const r = await axios.post(`${API}/me/referral-code`);
      setReferral(r.data);
      if (r.data.new) toast.success(`Created your referral code ${r.data.code} — check your email!`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not get your referral code");
    } finally {
      setReferralLoading(false);
    }
  };
  const copyReferral = async () => {
    if (!referral?.code) return;
    try {
      const url = `${window.location.origin}/?ref=${referral.code}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied!");
    } catch (_) {
      toast.error("Couldn't copy — try long-pressing the code");
    }
  };
  const shareReferral = async () => {
    if (!referral?.code) return;
    const url = `${window.location.origin}/?ref=${referral.code}`;
    const text = `Use my Link Ride referral code ${referral.code} for 25% off your first ride!`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Link Ride referral", text, url });
      } catch (_) {/* user cancelled */}
    } else {
      copyReferral();
    }
  };

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/account');
    }
  }, [authLoading, user, navigate]);

  // Handle post-Stripe-Checkout return: show toast + clear query string.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sub = params.get('subscription');
    if (sub === 'success') {
      toast.success('Link Pro active — your discount is now live on every booking');
      window.history.replaceState({}, '', '/account');
    } else if (sub === 'cancelled') {
      toast.info('Subscription not completed');
      window.history.replaceState({}, '', '/account');
    }
    const credits = params.get('credits');
    if (credits === 'success') {
      toast.success('Credits added to your wallet — thanks!');
      window.history.replaceState({}, '', '/account');
    } else if (credits === 'cancelled') {
      toast.info('Credit purchase cancelled');
      window.history.replaceState({}, '', '/account');
    }
  }, []);

  const fetchAddresses = async () => {
    try {
      const res = await axios.get(`${API}/me/addresses`);
      setAddresses(res.data);
    } catch (e) {
      toast.error('Failed to load addresses');
    } finally {
      setLoading(false);
    }
  };

  // Sort customer trip history newest-first (scheduled date+time → completed_at → created_at)
  const sortHistory = (rows) => {
    const ts = (b) => {
      if (b.scheduled_date) {
        const d = new Date(`${b.scheduled_date}T${b.scheduled_time || "00:00"}`);
        if (!isNaN(d.getTime())) return d.getTime();
      }
      if (b.completed_at) {
        const d = new Date(b.completed_at);
        if (!isNaN(d.getTime())) return d.getTime();
      }
      if (b.created_at) {
        const d = new Date(b.created_at);
        if (!isNaN(d.getTime())) return d.getTime();
      }
      return 0;
    };
    return [...(rows || [])].sort((a, b) => ts(b) - ts(a));
  };

  useEffect(() => {
    if (user) {
      fetchAddresses();
      axios.get(`${API}/me/bookings`).then((r) => setHistory(sortHistory(r.data))).catch(() => setHistory([]));
    }
  }, [user]);

  const setDefault = async (id) => {
    try {
      await axios.post(`${API}/me/addresses/${id}/default`);
      toast.success('Default pickup set');
      fetchAddresses();
    } catch {
      toast.error('Failed');
    }
  };

  const bookAgain = (b) => {
    // Store a one-shot prefill in sessionStorage so the customer booking page picks it up
    const prefill = {
      service_type: b.service_type,
      pickup: b.pickup_coords ? { address: b.pickup_address, coords: b.pickup_coords, city: b.pickup_city || '' } : null,
      dropoff: b.dropoff_coords ? { address: b.dropoff_address, coords: b.dropoff_coords, city: b.dropoff_city || '' } : null
    };
    sessionStorage.setItem('link_prefill', JSON.stringify(prefill));
    navigate('/');
  };

  const saveAddress = async () => {
    if (!newLabel.trim() || !newAddr.address) {
      toast.error('Label and address required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/me/addresses`, {
        label: newLabel.trim(),
        address: newAddr.address,
        city: newAddr.city,
        state: newAddr.state,
        coords: newAddr.coords
      });
      toast.success('Address saved');
      setAdding(false);
      setNewLabel('');
      setNewAddr({ address: '', coords: null, city: '', state: '' });
      fetchAddresses();
    } catch (e) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const removeAddress = async (id) => {
    try {
      await axios.delete(`${API}/me/addresses/${id}`);
      toast.success('Removed');
      fetchAddresses();
    } catch (e) {
      toast.error('Failed to remove');
    }
  };

  const ACTIVE_STATES = new Set(['pending', 'confirmed', 'in_progress', 'driver_assigned', 'en_route_pickup', 'at_pickup', 'in_transit']);
  const active = history.filter((b) => ACTIVE_STATES.has(b.status));
  const past = history.filter((b) => !ACTIVE_STATES.has(b.status));

  // Customer-initiated trip cancellation. Calls the same endpoint the admin
  // uses; backend allows cancel from any pre-completed state.
  const cancelTrip = async (bookingId) => {
    if (!window.confirm("Cancel this trip? This can't be undone.")) return;
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`);
      toast.success("Trip cancelled");
      const r = await axios.get(`${API}/me/bookings`);
      setHistory(sortHistory(r.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const STATUS_LABEL = {
    pending: 'Awaiting driver',
    confirmed: 'Driver confirmed',
    driver_assigned: 'Driver assigned',
    in_progress: 'Driver en route',
    en_route_pickup: 'En route to pickup',
    at_pickup: 'At pickup',
    in_transit: 'In transit',
    completed: 'Completed',
    cancelled: 'Cancelled'
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#0047FF] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Toaster position="top-center" toastOptions={{
        style: { background: '#121212', color: '#fafafa', border: '1px solid rgba(255,255,255,0.08)' }
      }} />

      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="text-white/60" data-testid="back-to-home">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>My Account</h1>
          </div>
          <Button variant="ghost" size="sm" onClick={() => { logout(); navigate('/'); }} className="text-white/60" data-testid="logout-button">
            <LogOut className="w-4 h-4 mr-1" /> Sign out
          </Button>
        </div>
      </header>

      <main className="px-5 py-6 max-w-lg mx-auto space-y-6">
        {/* Active Trips — prominent */}
        {active.length > 0 && (
          <div data-testid="active-trips-section">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#00E676] font-bold mb-3 flex items-center gap-2">
              <Radio className="w-3 h-3 animate-pulse" /> Active Trips
            </p>
            <div className="space-y-2">
              {active.map((b) => (
                <Card key={b.id} className="bg-[#121212] border-[#00E676]/40 shadow-[0_8px_32px_-12px_rgba(0,230,118,0.25)]" data-testid={`active-${b.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
                        <span className="text-xs text-[#00E676] font-medium">{STATUS_LABEL[b.status] || b.status}</span>
                      </div>
                      <span className="text-sm font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>
                        ${b.estimated_price?.toFixed(2) || '--'}
                      </span>
                    </div>
                    <p className="text-sm text-white truncate">{b.pickup_address}</p>
                    {Array.isArray(b.stops) && b.stops.length > 0 && b.stops.map((stop, idx) => (
                      <p key={idx} className="text-sm text-amber-300/90 truncate" data-testid={`active-stop-${idx}`}>↳ Stop {idx + 1}: {stop.address}</p>
                    ))}
                    <p className="text-sm text-white/60 truncate">→ {b.dropoff_address}</p>
                    <Button
                      onClick={() => navigate(`/track/${b.id}`)}
                      className="w-full mt-3 bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
                      data-testid={`track-active-${b.id}`}
                    >
                      <Navigation className="w-4 h-4 mr-1.5" /> Track Live
                    </Button>
                    <Button
                      onClick={() => setEditRouteBooking(b)}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 border-[#0047FF]/40 text-[#7AADFF] hover:bg-[#0047FF]/10"
                      data-testid={`edit-route-active-${b.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5 mr-1.5" />
                      Edit pickup / drop-off / stops
                    </Button>
                    <Button
                      onClick={() => cancelTrip(b.id)}
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 border-red-500/30 text-red-400 hover:bg-red-500/10"
                      data-testid={`cancel-active-${b.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel trip
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Profile */}
        <Card className="bg-[#121212] border-white/5">
          <CardContent className="p-5 space-y-4">
            <AvatarUploader
              user={user}
              onChange={(url) => patchUser({ avatar_url: url })}
            />
            <div className="pt-3 border-t border-white/5 space-y-3">
              {/* Preferred name — used on driver/rider cards, chat, notifications.
                  Falls back to first_name when not set. */}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">
                  Preferred name
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    key={`pref-${user.preferred_name || ''}`}
                    defaultValue={user.preferred_name || ""}
                    placeholder={user.first_name || "e.g. Bill"}
                    maxLength={40}
                    className="bg-[#050505] border-white/10 text-white flex-1"
                    data-testid="preferred-name-input"
                    onBlur={(e) => {
                      const next = e.target.value.trim();
                      if (next === (user.preferred_name || "")) return;
                      patchUser({ preferred_name: next || null });
                      toast.success(next ? `Riders and drivers will see you as "${next}".` : "Preferred name cleared.");
                    }}
                  />
                </div>
                <p className="text-[10px] text-white/40 mt-1">
                  Shown on driver / rider cards and in chat. Leave blank to use your legal first name.
                </p>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-white/70"><Mail className="w-3.5 h-3.5" /> {user.email}</div>
                <div className="flex items-center gap-2 text-white/70"><Phone className="w-3.5 h-3.5" /> {user.phone}</div>
                <div className="text-white/40 text-xs uppercase tracking-wider pt-1">
                  {user.role === 'customer' ? 'Rider' : user.role}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Saved Places */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold">Saved Places</p>
            {!adding && (
              <Button size="sm" onClick={() => setAdding(true)} className="bg-[#0047FF] hover:bg-[#003BE0] text-white rounded-lg" data-testid="add-address-button">
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            )}
          </div>

          {adding && (
            <Card className="bg-[#121212] border-[#0047FF]/30 mb-3">
              <CardContent className="p-4 space-y-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/50 font-bold">Label</Label>
                  <Input
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    placeholder="e.g. Home, Gym, Mom's"
                    maxLength={40}
                    className="bg-[#050505] border-white/10 text-white mt-1"
                    data-testid="new-address-label"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-white/50 font-bold">Address</Label>
                  <div className="mt-1">
                    <AddressAutocomplete
                      value={newAddr.address}
                      onChange={(address, coords, meta) =>
                        setNewAddr({ address, coords, city: meta?.city || '', state: meta?.state || '' })
                      }
                      placeholder="Start typing address…"
                      testId="new-address-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={saveAddress}
                    disabled={saving || !newLabel.trim() || !newAddr.address}
                    className="flex-1 bg-[#0047FF] hover:bg-[#003BE0] text-white border-0 disabled:opacity-40"
                    data-testid="save-address-button"
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    onClick={() => { setAdding(false); setNewLabel(''); setNewAddr({ address: '', coords: null, city: '', state: '' }); }}
                    variant="ghost"
                    className="text-white/60"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="text-center py-8 text-white/40">Loading…</div>
          ) : addresses.length === 0 ? (
            <Card className="bg-[#121212] border-white/5">
              <CardContent className="p-8 text-center">
                <MapPin className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50 text-sm">No saved places yet</p>
                <p className="text-white/30 text-xs mt-1">Save your home, office, gym — anywhere you go often.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {addresses.map((a) => (
                <Card key={a.id} className="bg-[#121212] border-white/5" data-testid={`saved-address-${a.id}`}>
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="p-2 rounded-lg bg-[#0047FF]/15 mt-0.5">
                      <MapPin className="w-4 h-4 text-[#4D88FF]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>{a.label}</p>
                        {a.is_default && (
                          <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-white/60 truncate">{a.address}</p>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setDefault(a.id)}
                      className={`${a.is_default ? 'text-[#00E676]' : 'text-white/40 hover:text-[#00E676]'} hover:bg-[#00E676]/10`}
                      data-testid={`default-address-${a.id}`}
                      title="Set as default pickup"
                    >
                      <Star className={`w-4 h-4 ${a.is_default ? 'fill-[#00E676]' : ''}`} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeAddress(a.id)}
                      className="text-white/40 hover:text-red-400 hover:bg-red-500/10"
                      data-testid={`delete-address-${a.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>

        {/* Booking History */}
        <SubscriptionCard />

        <LoyaltyCreditsCard subscriptionPlan={user?.subscription_plan_id} subscriptionStatus={user?.subscription_status} pricingLocked={user?.locked_pricing} />

        {/* Install as app on home screen */}
        <Card
          className="bg-gradient-to-br from-[#0047FF]/15 via-[#0a0a0a] to-[#0a0a0a] border-[#0047FF]/30 cursor-pointer hover:border-[#0047FF]/60 transition-colors"
          onClick={() => navigate('/install')}
          data-testid="install-app-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-xl bg-[#0047FF]/15 border border-[#0047FF]/30">
              <Smartphone className="w-5 h-5 text-[#0047FF]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>
                Install Link Ride on your home screen
              </p>
              <p className="text-xs text-white/60 mt-0.5">
                30-second setup · works like a real app · push alerts when your driver arrives
              </p>
            </div>
            <ArrowLeft className="w-4 h-4 text-[#0047FF] rotate-180 shrink-0" />
          </CardContent>
        </Card>

        {/* How Link Ride works — in-app walkthrough */}
        <Card
          className="bg-[#121212] border-white/10 cursor-pointer hover:border-white/25 transition-colors"
          onClick={() => navigate(user?.role === 'driver' || user?.role === 'admin' ? '/help?role=driver' : '/help')}
          data-testid="help-card"
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-xl bg-white/5 border border-white/10">
              <HelpCircle className="w-5 h-5 text-white/80" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>
                How Link Ride works
              </p>
              <p className="text-xs text-white/60 mt-0.5">
                Step-by-step walkthrough · booking, paying, redeeming points
              </p>
            </div>
            <ArrowLeft className="w-4 h-4 text-white/40 rotate-180 shrink-0" />
          </CardContent>
        </Card>


        {/* Referral code — invite friends, both win */}
        <Card className="bg-gradient-to-br from-amber-500/15 via-[#0a0a0a] to-[#0a0a0a] border-amber-500/30" data-testid="referral-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-2.5 rounded-xl bg-amber-500/15 border border-amber-500/30">
                <Gift className="w-5 h-5 text-amber-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>
                  Invite a friend, get them 25% off
                </p>
                <p className="text-xs text-white/60 mt-0.5">
                  Your unique code unlocks 25% off their first Link Ride trip. Valid 90 days.
                </p>
              </div>
            </div>
            {referral ? (
              <div className="space-y-2">
                <div className="bg-black/30 border-2 border-dashed border-amber-500/40 rounded-xl p-3 text-center" data-testid="referral-code-display">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-amber-300/70 font-bold">Your code</p>
                  <p className="text-2xl font-mono font-bold text-amber-300 mt-1 tracking-wider">{referral.code}</p>
                  <p className="text-[10px] text-zinc-500 mt-1">
                    {referral.used_count > 0 ? "Used — mint another via support" : `Single use · expires ${new Date(referral.expires_at).toLocaleDateString()}`}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={copyReferral} variant="outline" className="flex-1 border-amber-500/30 text-amber-300 hover:bg-amber-500/10" data-testid="referral-copy-button">
                    <Copy className="w-4 h-4 mr-1" /> Copy link
                  </Button>
                  <Button onClick={shareReferral} className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold" data-testid="referral-share-button">
                    Share
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                onClick={fetchOrMintReferral}
                disabled={referralLoading}
                className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold"
                data-testid="referral-get-button"
              >
                {referralLoading ? "Generating…" : "Get my referral code"}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Become a Driver — only for non-driver accounts */}
        {user && user.role !== 'driver' && (
          <Card className="bg-gradient-to-br from-[#0047FF]/15 via-[#0a0a0a] to-[#0a0a0a] border-[#0047FF]/30" data-testid="become-driver-card">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-[#0047FF]/15 border border-[#0047FF]/30">
                <Truck className="w-6 h-6 text-[#4D88FF]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, system-ui' }}>
                  Earn money driving with Link Ride
                </p>
                <p className="text-xs text-white/60 mt-1">
                  Add driver capabilities to your existing account — no new email needed.
                </p>
              </div>
              <Button
                onClick={() => navigate('/become-driver')}
                className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold whitespace-nowrap"
                data-testid="become-driver-btn"
              >
                Get Started
              </Button>
            </CardContent>
          </Card>
        )}

        <PushToggle description="Get notified the moment your driver accepts and arrives." />

        {past.length > 0 && (
          <div data-testid="history-section">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-bold mb-3">Recent Trips</p>
            <div className="space-y-2">
              {past.slice(0, 10).map((b) => (
                <Card key={b.id} className="bg-[#121212] border-white/5" data-testid={`history-${b.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-3.5 h-3.5 text-white/40" />
                        <span className="text-xs text-white/60">
                          {new Date(b.created_at).toLocaleDateString()}
                        </span>
                        <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white/5 text-white/50 border border-white/10">
                          {b.status}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-[#00E676]" style={{ fontFamily: 'Outfit, system-ui' }}>
                        ${b.estimated_price?.toFixed(2) || '--'}
                      </span>
                    </div>
                    <p className="text-sm text-white truncate">{b.pickup_address}</p>
                    {Array.isArray(b.stops) && b.stops.length > 0 && b.stops.map((stop, idx) => (
                      <p key={idx} className="text-sm text-amber-300/90 truncate" data-testid={`history-stop-${idx}`}>↳ Stop {idx + 1}: {stop.address}</p>
                    ))}
                    <p className="text-sm text-white/60 truncate">→ {b.dropoff_address}</p>
                    {b.rating ? (
                      <div className="flex items-center gap-1 mt-2 text-yellow-400">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star
                            key={n}
                            className={`w-3.5 h-3.5 ${n <= b.rating ? 'fill-yellow-400' : 'text-zinc-700'}`}
                          />
                        ))}
                        {b.tip > 0 && (
                          <span className="text-xs text-white/50 ml-2">· ${b.tip.toFixed(2)} tip</span>
                        )}
                      </div>
                    ) : b.status === 'completed' ? (
                      <Button
                        size="sm"
                        onClick={() => setRateTarget(b)}
                        className="w-full mt-3 bg-yellow-500/15 hover:bg-yellow-500/25 text-yellow-300 border border-yellow-500/30 rounded-lg"
                        data-testid={`rate-trip-${b.id}`}
                      >
                        <Star className="w-3.5 h-3.5 mr-1.5" /> Rate &amp; tip your driver
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      onClick={() => bookAgain(b)}
                      className="w-full mt-2 bg-[#0047FF]/15 hover:bg-[#0047FF]/25 text-[#4D88FF] border border-[#0047FF]/30 rounded-lg"
                      data-testid={`book-again-${b.id}`}
                    >
                      <Repeat className="w-3.5 h-3.5 mr-1.5" /> Book again
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>
      <RatingTipDialog
        booking={rateTarget}
        open={!!rateTarget}
        onOpenChange={(o) => !o && setRateTarget(null)}
        onSubmitted={() => {
          setRateTarget(null);
          axios.get(`${API}/me/bookings`).then((r) => setHistory(sortHistory(r.data))).catch(() => {});
        }}
      />
      <EditRouteDialog
        open={!!editRouteBooking}
        booking={editRouteBooking}
        onClose={() => setEditRouteBooking(null)}
        onSaved={() => {
          // Refresh active-trips list so the new pickup/drop-off shows
          axios.get(`${API}/me/bookings`).then((r) => setHistory(sortHistory(r.data))).catch(() => {});
        }}
      />
    </div>
  );
}
